import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiSave, 
  FiX, 
  FiAlertCircle,
  FiBookOpen
} from 'react-icons/fi';
import api from '../../services/api';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.75rem, 2.5vw + 1rem, 2.5rem);
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const AddButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
    font-size: 0.875rem;
  }
`;

const BulkPricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BulkPricingCard = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
  }
`;

const CardInfo = styled.div`
  flex: 1;
`;

const RuleName = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Description = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
`;

const DiscountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const DiscountValue = styled.span`
  font-weight: 600;
  color: var(--primary-color);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? 'var(--primary-color)' : props.variant === 'delete' ? 'var(--error-color)' : 'var(--background-secondary)'};
  color: ${props => props.variant === 'delete' ? 'white' : 'var(--text-primary)'};
  border: none;
  border-radius: 0;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'edit' ? 'var(--primary-hover)' : props.variant === 'delete' ? 'var(--error-dark)' : 'var(--border-color)'};
    transform: translateY(-1px);
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? 'var(--success-color)' : 'var(--error-color)'};
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  min-height: 80px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' && `
    background: var(--primary-color);
    color: var(--text-inverse);
    border: none;

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-1px);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--border-color);
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  padding: 1rem;
  border-radius: 0;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function BulkPricingManagement() {
  const [bulkPricingRules, setBulkPricingRules] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    min_courses: '',
    max_courses: '',
    discount_type: 'percentage',
    discount_value: '',
    is_active: true,
    applicable_courses: [],
    apply_to_all_courses: true
  });

  useEffect(() => {
    fetchBulkPricingRules();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.getCourses();
      setCourses(response.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchBulkPricingRules = async () => {
    try {
      setLoading(true);
      const response = await api.getBulkPricingRules();
      setBulkPricingRules(response.bulkPricingRules);
    } catch (error) {
      console.error('Error fetching bulk pricing rules:', error);
      setError('Failed to load bulk pricing rules');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      min_courses: '',
      max_courses: '',
      discount_type: 'percentage',
      discount_value: '',
      is_active: true,
      applicable_courses: [],
      apply_to_all_courses: true
    });
    setShowModal(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    
    // Parse JSON fields safely
    let applicableCourses = [];
    try {
      if (rule.applicable_courses) {
        applicableCourses = JSON.parse(rule.applicable_courses);
      }
    } catch (e) {
      console.error('Error parsing applicable_courses:', e);
    }
    
    setFormData({
      name: rule.name,
      description: rule.description || '',
      min_courses: rule.min_courses,
      max_courses: rule.max_courses || '',
      discount_type: rule.discount_type,
      discount_value: rule.discount_value,
      is_active: rule.is_active,
      applicable_courses: applicableCourses,
      apply_to_all_courses: rule.apply_to_all_courses !== undefined ? Boolean(rule.apply_to_all_courses) : true
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this bulk pricing rule?')) {
      try {
        await api.deleteBulkPricingRule(id);
        await fetchBulkPricingRules();
      } catch (error) {
        console.error('Error deleting bulk pricing rule:', error);
        setError('Failed to delete bulk pricing rule');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        min_courses: parseInt(formData.min_courses),
        max_courses: formData.max_courses ? parseInt(formData.max_courses) : null,
        discount_value: parseFloat(formData.discount_value),
        applicable_courses: formData.apply_to_all_courses ? null : formData.applicable_courses,
        apply_to_all_courses: formData.apply_to_all_courses
      };

      if (editingRule) {
        await api.updateBulkPricingRule(editingRule.id, submitData);
      } else {
        await api.createBulkPricingRule(submitData);
      }

      setShowModal(false);
      await fetchBulkPricingRules();
    } catch (error) {
      console.error('Error saving bulk pricing rule:', error);
      setError('Failed to save bulk pricing rule');
    }
  };

  const handleCourseSelection = (courseId, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        applicable_courses: [...prev.applicable_courses, parseInt(courseId)]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        applicable_courses: prev.applicable_courses.filter(id => id !== parseInt(courseId))
      }));
    }
  };

  const formatDiscount = (type, value) => {
    // Ensure value is a number and remove any existing symbols
    const numericValue = parseFloat(value);
    if (type === 'percentage') {
      return `${numericValue}%`;
    } else {
      return `$${numericValue.toFixed(2)}`;
    }
  };

  const formatCourseRange = (min, max) => {
    if (max) {
      return `${min}-${max} courses`;
    } else {
      return `${min}+ courses`;
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading bulk pricing rules...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Bulk Pricing Management</Title>
        <AddButton onClick={handleAddNew}>
          <FiPlus size={16} />
          Add Bulk Pricing Rule
        </AddButton>
      </Header>

      {error && (
        <ErrorMessage>
          <FiAlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      <BulkPricingGrid>
        {bulkPricingRules.map((rule) => (
          <BulkPricingCard
            key={rule.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CardHeader>
              <CardInfo>
                <RuleName>
                  <FiBookOpen size={20} />
                  {rule.name}
                </RuleName>
                {rule.description && <Description>{rule.description}</Description>}
                <DiscountInfo>
                  <DiscountValue>
                    {formatDiscount(rule.discount_type, rule.discount_value)}
                  </DiscountValue>
                </DiscountInfo>
              </CardInfo>
              <CardActions>
                <ActionButton variant="edit" onClick={() => handleEdit(rule)}>
                  <FiEdit size={16} />
                </ActionButton>
                <ActionButton variant="delete" onClick={() => handleDelete(rule.id)}>
                  <FiTrash2 size={16} />
                </ActionButton>
              </CardActions>
            </CardHeader>
            <CardBody>
              <InfoRow>
                <InfoLabel>Status</InfoLabel>
                <StatusBadge active={rule.is_active}>
                  {rule.is_active ? 'Active' : 'Inactive'}
                </StatusBadge>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Course Range</InfoLabel>
                <InfoValue>{formatCourseRange(rule.min_courses, rule.max_courses)}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Applicable To</InfoLabel>
                <InfoValue>
                  {rule.apply_to_all_courses 
                    ? 'All courses' 
                    : (() => {
                        try {
                          const applicableCourses = rule.applicable_courses ? JSON.parse(rule.applicable_courses) : [];
                          return applicableCourses.length > 0 
                            ? `${applicableCourses.length} selected course(s)` 
                            : 'No courses selected';
                        } catch (e) {
                          return 'All courses';
                        }
                      })()}
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>Discount</InfoLabel>
                <InfoValue>{formatDiscount(rule.discount_type, rule.discount_value)}</InfoValue>
              </InfoRow>
            </CardBody>
          </BulkPricingCard>
        ))}
      </BulkPricingGrid>

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalHeader>
              <ModalTitle>
                {editingRule ? 'Edit Bulk Pricing Rule' : 'Add New Bulk Pricing Rule'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Rule Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., 2 Course Bundle"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this bulk pricing rule..."
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Min Courses *</Label>
                  <Input
                    type="number"
                    min="2"
                    value={formData.min_courses}
                    onChange={(e) => setFormData({ ...formData, min_courses: e.target.value })}
                    placeholder="2"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Max Courses (optional)</Label>
                  <Input
                    type="number"
                    min="2"
                    value={formData.max_courses}
                    onChange={(e) => setFormData({ ...formData, max_courses: e.target.value })}
                    placeholder="Leave empty for unlimited"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Discount Type *</Label>
                  <Select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                    required
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed_amount">Fixed Amount</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                    placeholder={formData.discount_type === 'percentage' ? '15' : '25.00'}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Applicable Courses</Label>
                <div style={{ marginBottom: '1rem' }}>
                  <Label>
                    <input
                      type="checkbox"
                      checked={formData.apply_to_all_courses}
                      onChange={(e) => setFormData({ ...formData, apply_to_all_courses: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Apply to all courses
                  </Label>
                </div>
                {!formData.apply_to_all_courses && (
                  <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border-color)', padding: '0.5rem', borderRadius: 0}}>
                    {courses.length === 0 ? (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading courses...</div>
                    ) : (
                      courses.map(course => (
                        <div key={course.id} style={{ marginBottom: '0.5rem' }}>
                          <Label>
                            <input
                              type="checkbox"
                              checked={formData.applicable_courses.includes(course.id)}
                              onChange={(e) => handleCourseSelection(course.id, e.target.checked)}
                              style={{ marginRight: '0.5rem' }}
                            />
                            {course.name} ({course.code})
                          </Label>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Active
                </Label>
              </FormGroup>

              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  <FiSave size={16} />
                  {editingRule ? 'Update' : 'Create'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default BulkPricingManagement;
